import { Stethoscope, Brain, Scale, ClipboardList, FolderCog, GraduationCap, ShieldCheck, HeartPulse, ArrowRight, Download } from "lucide-react";
import { Reveal, SectionHeading } from "@/components/site/Reveal";
import { Sprig } from "@/components/site/Logo";
import { API, GENERAL_FORM_URL, FAMILY_LAW_FORM_URL } from "@/data/services";

const PATHWAYS = [
  { icon: Stethoscope, title: "GPs", text: "Refer with a Mental Health Treatment Plan for Medicare-rebated individual therapy, or enquire about any of our services." },
  { icon: Brain, title: "Psychiatrists & psychologists", text: "Collaborative referrals for trauma-informed therapy, couples work and family therapy." },
  { icon: Scale, title: "Solicitors", text: "Independent family reports, parental capacity assessments and parent–child observations, with clear timeframes and fees." },
  { icon: ClipboardList, title: "NDIS support coordinators", text: "Specialist Positive Behaviour Support referrals — functional behaviour assessment through to plan implementation and review." },
  { icon: FolderCog, title: "Plan managers", text: "Service agreements and invoicing for plan-managed participants under Capacity Building — Improved Relationships." },
  { icon: GraduationCap, title: "Schools & child services", text: "Therapy and assessment for children and young people, including play-based and developmentally informed approaches." },
  { icon: ShieldCheck, title: "Open Arms & veteran services", text: "Referrals for current and former ADF members, veterans, partners and defence families." },
  { icon: HeartPulse, title: "Allied health professionals", text: "Complementary care for shared clients, with consent-based communication and clear role boundaries." },
];

const ACTIONS = [
  {
    title: "Therapy referral",
    text: "Individual, couples, family or veteran therapy — send through the referral and we will contact the client directly.",
    primary: { label: "Make a Therapy Referral", href: GENERAL_FORM_URL, id: "referrer-therapy-button" },
  },
  {
    title: "NDIS referral",
    text: "Positive Behaviour Support for NDIS participants. Complete the referral form and return it by email.",
    primary: { label: "Make an NDIS Referral", href: GENERAL_FORM_URL, id: "referrer-ndis-button" },
    secondary: { label: "Download Referral Form", href: `${API}/downloads/ndis-referral-form`, id: "referrer-ndis-download" },
  },
  {
    title: "Family law assessment",
    text: "Independent reports and assessments for family law matters. An information pack for referrers is available.",
    primary: { label: "Family Law Assessment Enquiry", href: FAMILY_LAW_FORM_URL, id: "referrer-family-law-button" },
    secondary: { label: "Download Information for Referrers", href: `${API}/downloads/family-law-referrer-info`, id: "referrer-family-law-download" },
  },
];

export default function ForReferrers() {
  return (
    <main data-testid="referrers-page">
      <section className="relative overflow-hidden bg-white">
        <div className="pointer-events-none absolute -right-10 -top-10 text-euca-badge">
          <Sprig className="h-64 w-64" />
        </div>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20 sm:py-28 relative">
          <SectionHeading
            eyebrow="For Referrers"
            title="Clear pathways for professionals"
            lead="Referral information for GPs, psychiatrists, psychologists, solicitors, NDIS support coordinators, plan managers, schools, veteran services and allied health professionals."
          />
        </div>
      </section>

      <section className="bg-euca-mist">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16 sm:py-24">
          <div className="grid gap-6 sm:grid-cols-2 lg:grid-cols-4">
            {PATHWAYS.map((p, i) => (
              <Reveal key={p.title} delay={(i % 4) * 0.06}>
                <div className="h-full rounded-2xl border border-euca-line bg-white p-7 shadow-sm transition-all duration-300 hover:-translate-y-1 hover:border-euca-sage hover:shadow-lg">
                  <p.icon className="h-6 w-6 text-euca" strokeWidth={1.5} />
                  <h3 className="mt-4 font-serif text-xl font-medium text-euca-dark">{p.title}</h3>
                  <p className="mt-2 text-sm leading-relaxed text-mutedsoft">{p.text}</p>
                </div>
              </Reveal>
            ))}
          </div>

          <div className="mt-16 grid gap-6 lg:grid-cols-3">
            {ACTIONS.map((a, i) => (
              <Reveal key={a.title} delay={i * 0.08}>
                <div className="flex h-full flex-col rounded-2xl bg-euca-dark p-8">
                  <h3 className="font-serif text-2xl font-medium text-euca-mist">{a.title}</h3>
                  <p className="mt-3 flex-1 text-sm leading-relaxed text-euca-mist/70">{a.text}</p>
                  <div className="mt-6 space-y-3">
                    <a
                      href={a.primary.href}
                      target={a.primary.href.startsWith("http") ? "_blank" : undefined}
                      rel={a.primary.href.startsWith("http") ? "noopener noreferrer" : undefined}
                      data-testid={a.primary.id}
                      className="flex items-center justify-center gap-2 rounded-full bg-euca-mist px-6 py-3 text-sm font-semibold text-euca-dark transition-all duration-300 hover:bg-white"
                    >
                      {a.primary.label} <ArrowRight className="h-4 w-4" />
                    </a>
                    {a.secondary && (
                      <a
                        href={a.secondary.href}
                        data-testid={a.secondary.id}
                        className="flex items-center justify-center gap-2 rounded-full border border-euca-mist/40 px-6 py-3 text-sm font-semibold text-euca-mist transition-all duration-300 hover:border-euca-mist"
                      >
                        {a.secondary.label} <Download className="h-4 w-4" />
                      </a>
                    )}
                  </div>
                </div>
              </Reveal>
            ))}
          </div>

          <Reveal delay={0.1}>
            <p className="mt-14 max-w-2xl text-base leading-relaxed text-mutedsoft">
              Prefer to talk first? Call <a href="tel:+61417508502" className="font-semibold text-euca hover:underline" data-testid="referrers-phone">0417 508 502</a> or
              email <a href="mailto:hello@eucalypttherapy.com.au" className="font-semibold text-euca hover:underline" data-testid="referrers-email">hello@eucalypttherapy.com.au</a> to
              discuss a referral before sending it through.
            </p>
          </Reveal>
        </div>
      </section>
    </main>
  );
}
